import { useState, type FormEvent } from 'react';
import type { AlertRule, Signal, TriggeredAlert } from '../types/market';

interface AlertCenterProps {
  symbols: string[];
  rules: AlertRule[];
  history: TriggeredAlert[];
  onAdd: (symbol: string, type: AlertRule['type'], value: number | Signal) => void;
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
  onClearHistory: () => void;
  onEnableNotifications: () => Promise<NotificationPermission | 'unsupported'>;
}

export function AlertCenter({
  symbols,
  rules,
  history,
  onAdd,
  onRemove,
  onToggle,
  onClearHistory,
  onEnableNotifications
}: AlertCenterProps) {
  const [symbol, setSymbol] = useState(symbols[0] ?? 'AAPL');
  const [type, setType] = useState<AlertRule['type']>('PRICE_ABOVE');
  const [value, setValue] = useState('');
  const [notificationState, setNotificationState] = useState<NotificationPermission | 'unsupported'>(() => 'Notification' in window ? Notification.permission : 'unsupported');

  function submit(event: FormEvent) {
    event.preventDefault();
    if (type === 'SIGNAL') {
      const signal = (value || 'BUY').toUpperCase() as Signal;
      if (!['BUY', 'SELL', 'HOLD'].includes(signal)) return;
      onAdd(symbol, type, signal);
      setValue('');
      return;
    }
    const price = Number(value);
    if (!Number.isFinite(price) || price <= 0) return;
    onAdd(symbol, type, price);
    setValue('');
  }

  return (
    <section className="alerts-panel">
      <div className="panel-heading-row">
        <div>
          <span className="eyebrow">Local monitoring</span>
          <h2>Alert Center</h2>
        </div>
        <button className="action-link" type="button" onClick={() => void onEnableNotifications().then(setNotificationState)}>
          Notifications: {notificationState}
        </button>
      </div>

      <form className="alert-form" onSubmit={submit}>
        <select value={symbol} onChange={(event) => setSymbol(event.target.value)}>
          {symbols.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={type} onChange={(event) => { setType(event.target.value as AlertRule['type']); setValue(''); }}>
          <option value="PRICE_ABOVE">Price above</option>
          <option value="PRICE_BELOW">Price below</option>
          <option value="SIGNAL">Strategy signal</option>
        </select>
        {type === 'SIGNAL' ? (
          <select value={value || 'BUY'} onChange={(event) => setValue(event.target.value)}>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
            <option value="HOLD">HOLD</option>
          </select>
        ) : (
          <input type="number" min="0" step="any" value={value} onChange={(event) => setValue(event.target.value)} placeholder="Target price" />
        )}
        <button className="action-link primary" type="submit">Add alert</button>
      </form>

      <div className="alert-columns">
        <div>
          <div className="subheading-row"><strong>Rules</strong><span>{rules.filter((rule) => rule.enabled).length} active</span></div>
          <div className="alert-list">
            {rules.length === 0 ? <div className="empty-mini">No alerts yet.</div> : rules.map((rule) => (
              <article className="alert-rule" key={rule.id}>
                <button className={`toggle ${rule.enabled ? 'on' : ''}`} type="button" aria-label="Toggle alert" onClick={() => onToggle(rule.id)}><span /></button>
                <div>
                  <strong>{rule.symbol}</strong>
                  <span>{describeRule(rule)}</span>
                </div>
                <button className="icon-button danger-text" type="button" onClick={() => onRemove(rule.id)}>×</button>
              </article>
            ))}
          </div>
        </div>

        <div>
          <div className="subheading-row"><strong>Triggered</strong><button className="text-button" type="button" onClick={onClearHistory}>Clear</button></div>
          <div className="alert-list history-list">
            {history.length === 0 ? <div className="empty-mini">Nothing triggered yet.</div> : history.slice(0, 12).map((item) => (
              <article className="history-item" key={item.id}>
                <strong>{item.symbol}</strong>
                <span>{item.message}</span>
                <time>{new Date(item.triggeredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function describeRule(rule: AlertRule): string {
  if (rule.type === 'PRICE_ABOVE') return `Price ≥ ${Number(rule.value).toFixed(2)}`;
  if (rule.type === 'PRICE_BELOW') return `Price ≤ ${Number(rule.value).toFixed(2)}`;
  return `Signal = ${String(rule.value)}`;
}
